import React from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Formik, Field } from 'formik';
import * as yup from 'yup';
import { debounce, get } from 'lodash';

import './SendForSignatures.scss';
import {
  createSignatureRequest, getEmployees, clearEmployeeData,
  LOADING_PROP_TYPE, CREATE_SIGNATURE_REQUEST, GET_EMPLOYEES,
} from '../../../modules';
import { Spinner, SubmitButton, CloseButton, CenteredModal } from '../..';
import { SEARCH_EMPLOYEES_DEBOUNCE_TIME, APPLICATION_ROOT } from '../../../constants';

class SendForSignatures extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      allEmployees: true,
      selectedEmployees: [],
      placeholder: this.props.intl.formatMessage({ id: 'ENTER_EMPLOYEE_NAME_OR_ID_NUMBER' }),
      openMenu: false,
    };
  }

  onChange(selectedEmployees) {
    this.setState({ selectedEmployees });
    this.setState({ openMenu: false });
  }

  loadMoreEmployees = (query) => {
    const { employees: { next }, loading } = this.props;
    if (loading[GET_EMPLOYEES] === 'loading') return;
    this.props.clearEmployeeData();
    this.props.getEmployees(next, get(window, 'asure.appSettings.selectedCompany.companyId'), query);
  };

  submitRequest() {
    const { selectedDocument: { title, isHelloSignTemplate } } = this.props;
    const emailAddressToaster = (successes, failures) => (
      isHelloSignTemplate ?
        <div>
          <FormattedMessage
            id="SIGNATURE_REQUEST_SENT_TO_EMPLOYEES"
            values={{
              title,
              employeeCount: successes,
              plural: successes > 1 ? 's' : '',
            }}
            defaultMessage="SIGNATURE_REQUEST_SENT"
          />
          <br />
          <FormattedMessage
            id="EMPLOYEE_MISSING_EMAIL_WARNING_MESSAGE"
            values={{
              failures,
              failurePlural: failures > 1 ? 's' : '',
            }}
            defaultMessage="EMPLOYEE_MISSING_EMAIL_GENERIC_WARNING_MESSAGE"
          />
        </div>
        :
        <div>
          <FormattedMessage
            id="SIGNATURE_REQUEST_SENT_TO_EMPLOYEES"
            values={{
              title,
              employeeCount: successes + failures,
              plural: successes + failures > 1 ? 's' : '',
            }}
            defaultMessage="SIGNATURE_REQUEST_SENT"
          />
        </div>
    );

    this.props.createSignatureRequest(
      this.props.selectedDocument.id,
      emailAddressToaster,
      this.state.allEmployees,
      this.state.selectedEmployees,
      !isHelloSignTemplate // hr.services expects "isSimpleSign"
    );
  }

  render() {
    const {
      toggle,
      employees: {
        results: employees,
        searchResults: employeeSearchResults,
      },
      selectedDocument: {
        title,
      },
      loading,
      intl,
      isOpen,
    } = this.props;

    const isLoading = loading[CREATE_SIGNATURE_REQUEST] === 'loading';
    const isLoadingEmployees = loading[GET_EMPLOYEES] === 'loading';
    const activeEmployees = employees.filter(employee => employee.isActive);
    const employeeList = activeEmployees ?
      activeEmployees.map(employee => ({
        value: employee.eeCode,
        label: `${employee.firstName} ${employee.lastName} (${employee.eeCode})`,
      }))
      :
      [];
    const filteredEmployeeList = employeeSearchResults ?
      employeeSearchResults.map(employee => ({
        value: employee.eeCode,
        label: `${employee.firstName} ${employee.lastName} (${employee.eeCode})`,
      }))
      :
      [];

    const validationSchema = yup.object().shape({
      employeeSelection: yup.array(yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      }))
        .min(1, 'Please select at least one employee.'),
    });

    const debounceGetEmployees =
      debounce((query) => {
        this.loadMoreEmployees(query);
        this.setState({ openMenu: true });
      }, SEARCH_EMPLOYEES_DEBOUNCE_TIME);

    return (
      <CenteredModal
        style={{ margin: '10rem auto', maxWidth: '600px' }}
        isOpen={isOpen}
        parentselector={APPLICATION_ROOT}
        toggle={() => toggle()}
        backdrop
      >
        <div className="container px-4 py-4 rounded">
          {/* we use a negative margin on the header so that the bottom border extends all the way to the edge of the container */}
          <div className="modal-header pt-0 pb-4 mx-n4 px-4">
            <p className="font-weight-light h4 mb-0">
              <FormattedMessage id="SEND_FOR_SIGNATURES" />
            </p>
            <CloseButton onClick={toggle} />
          </div>

          <Formik
            validationSchema={validationSchema}
            isInitialValid={false}
            onSubmit={() => {}}
            initialValues={{ employeeSelection: [] }}
          >
            {({ errors, touched, setFieldTouched }) => (
              <div>
                <p className="mb-0 pt-4">
                  <FormattedMessage id="DOCUMENT_TO_BE_SIGNED" />
                </p>
                <p className="pt-2 text-muted">{title}</p>

                <p className="mb-0 pt-2">
                  <FormattedMessage id="WHO_NEEDS_TO_SIGN" />
                </p>

                <FormGroup className="employeeRadios pt-2 mb-0">
                  <FormGroup
                    check
                    className="pl-3"
                  >
                    <Label className="text-muted" check>
                      <Input
                        type="radio"
                        name="radio1"
                        checked={this.state.allEmployees}
                        onChange={(e) => {
                          this.setState({ allEmployees: e.target.checked });
                          setFieldTouched('employeeSelection', false);
                        }}
                      />
                      <FormattedMessage
                        id="ALL_EMPLOYEES"
                        // TODO: update message to show the total count of employees for this company
                        // id="ALL_EMPLOYEES_COUNT"
                        // values={{
                        //   employeeCount: totalCount,
                        // }}
                        // defaultMessage="ALL_EMPLOYEES"
                      />
                    </Label>
                  </FormGroup>
                  <FormGroup
                    check
                    className="pt-2 pl-3"
                  >
                    <Label className="text-muted" check>
                      <Input
                        type="radio"
                        name="radio1"
                        checked={!this.state.allEmployees}
                        onChange={e => this.setState({ allEmployees: !e.target.checked })}
                      />
                      <FormattedMessage className="text-muted" id="ONLY_SOME_EMPLOYEES" />
                    </Label>
                  </FormGroup>
                  <FormGroup className="pl-3 pt-1">
                    <Field
                      name="employeeSelection"
                      render={({ form }) => (<Select
                        type="select"
                        name="select"
                        id="empSelect"
                        isMulti
                        options={filteredEmployeeList.length > 0 ? filteredEmployeeList : employeeList}
                        components={{ LoadingIndicator: Spinner, DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                        classNamePrefix="react-select-container"
                        className={
                          errors.employeeSelection && touched.employeeSelection
                            ? 'form-control is-invalid p-0 h-100 mb-0'
                            : 'react-select-container form-control p-0 h-100 mb-0 border-0'
                        }
                        defaultValue={undefined}
                        isDisabled={this.state.allEmployees}
                        isLoading={isLoadingEmployees}
                        menuIsOpen={this.state.openMenu}
                        placeholder={this.state.placeholder}
                        onChange={(option) => {
                          form.setFieldValue('employeeSelection', option);
                          this.onChange(option);
                        }}
                        onFocus={() => {
                          this.setState({ placeholder: '' });
                        }}
                        onBlur={() => {
                          this.setState({ placeholder: intl.formatMessage({ id: 'ENTER_EMPLOYEE_NAME_OR_ID_NUMBER' }) });
                          form.setFieldTouched('employeeSelection', true);
                          this.setState({ openMenu: false });
                        }}
                        onInputChange={(query) => {
                          if (query === '') {
                            this.setState({ openMenu: false });
                          } else {
                            debounceGetEmployees(query);
                          }
                        }}
                      />)}
                    />
                    <small
                      id="errorState"
                      className="form-text invalid-feedback"
                      style={
                        errors.employeeSelection && touched.employeeSelection ? { display: 'block' } : undefined
                      }
                    >
                      {errors.employeeSelection}
                    </small>
                  </FormGroup>
                  <div className="d-flex flex-row-reverse">
                    {/* These are in "reversed" order so thats why Send Request comes before cancel here */}
                    <SubmitButton
                      onSubmit={() => {
                        if (!this.state.allEmployees && !touched.employeeSelection) {
                          setFieldTouched('employeeSelection', true);
                        } else if (this.state.allEmployees || !errors.employeeSelection) {
                          this.submitRequest();
                        }
                      }}
                      defaultLabel="SEND_REQUEST"
                      submittingLabel="LOADING"
                      disabled={isLoading}
                      submitting={isLoading}
                    />
                    <Button className="mr-3" color="default" onClick={toggle}>
                      <FormattedMessage id="CANCEL" />
                    </Button>
                  </div>
                </FormGroup>
              </div>
            )}
          </Formik>
        </div>
      </CenteredModal>
    );
  }
}

SendForSignatures.defaultProps = {
  selectedDocument: {},
};

SendForSignatures.propTypes = {
  loading: LOADING_PROP_TYPE.isRequired,
  createSignatureRequest: PropTypes.func.isRequired,
  getEmployees: PropTypes.func.isRequired,
  selectedDocument: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  clearEmployeeData: PropTypes.func.isRequired,
  employees: PropTypes.shape({
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    next: PropTypes.string,
    isActive: PropTypes.bool,
    searchResults: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
  }).isRequired,
  intl: intlShape.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};


export default connect(
  (state) => {
    const {
      resources: {
        employees,
        docs: {
          selectedDocument,
        },
      },
      appState: { loading },
    } = state;
    return {
      selectedDocument,
      employees,
      loading,
    };
  },
  {
    createSignatureRequest,
    getEmployees,
    clearEmployeeData,
  }
)(injectIntl(SendForSignatures));



// WEBPACK FOOTER //
// ./src/components/Forms/SendForSignatures/SendForSignatures.jsx